// apps/frontend/src/theme/ant-theme.ts
// 全站 Ant Design Vue 主题集中定义（阶段 A 核心）
// token 值与 docs/design-system.md 第二~四节严格一致，供暗色模式预留与全站复用。
import { theme as antdTheme } from 'ant-design-vue';
// ThemeConfig 未从包顶层导出，从其定义所在的 config-provider 上下文按类型导入
import type { ThemeConfig } from 'ant-design-vue/es/config-provider/context';

/** 统一字体栈（去 Google Fonts CDN，对齐规范 3.1，标题与正文同族仅靠字重区分） */
export const FONT_FAMILY =
  "system-ui, -apple-system, 'PingFang SC', 'Microsoft YaHei', sans-serif";

/**
 * 全站 Ant Design Vue 主题
 * - algorithm 预留：亮色 defaultAlgorithm；后续暗色切 darkAlgorithm 即可（本期不实现）
 * - token 语义命名，禁止散落 !important 覆盖，Ant 组件样式只走 ConfigProvider
 */
export const antTheme: ThemeConfig = {
  algorithm: antdTheme.defaultAlgorithm, // 暗色预留切换点
  token: {
    colorPrimary: '#4f46e5', // 品牌主色（规范 2.1 brand-500，唯一交互锚点色）
    colorInfo: '#06b6d4', // 提示（规范 2.3 info）
    colorSuccess: '#10b981', // 成功 / 已完成（规范 2.3 success）
    colorWarning: '#f59e0b', // 警告 / 待处理（规范 2.3 warning）
    colorError: '#ef4444', // 错误 / 删除（规范 2.3 danger）
    colorTextBase: '#1e293b', // 主要文字，禁用纯黑（规范 2.2 text-primary）
    colorBorder: '#e2e8f0', // 分割线、边框（规范 2.2 border-default）
    colorBgLayout: '#f8fafc', // 页面主背景（规范 2.2 bg-base）
    borderRadius: 6, // 按钮默认圆角（规范 圆角表）
    fontSize: 13, // UI 默认字号（规范 3.2 text-sm）
    fontFamily: FONT_FAMILY,
    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)', // 去彩色/多层阴影，收敛为极浅中性阴影
    boxShadowSecondary: '0 2px 8px rgba(0,0,0,0.06)', // 下拉/弹层，克制
    // 主/危险按钮的默认彩色描边阴影由种子 token controlOutline / colorErrorOutline
    // 生成（adv 4.x：boxShadow = 0 controlOutlineWidth 0 controlOutline），
    // 组件级 Button.primaryShadow 在该版本不生效，故在此将描边色设为透明，
    // 全站主按钮即无彩色阴影（规范 1.5）。
    controlOutline: 'transparent', // 去主按钮淡彩描边阴影
    colorErrorOutline: 'transparent', // 去危险按钮淡彩描边阴影
    wireframe: false,
  },
  // 注：ant-design-vue 4.2.6 的组件级 ComponentToken 类型定义大多为空接口（如
  // Button/Table 的 primaryShadow、headerBg 等字段运行时生效但类型未声明），
  // 此处按 antd 官方 token 语义配置，用类型断言绕过库类型定义缺失。
  components: {
    Button: {
      primaryShadow: 'none', // 去主按钮彩色阴影
      defaultShadow: 'none', // 去次按钮阴影
      fontWeight: 500, // 字重收敛（规范 3.3 仅 400/500/600）
      controlHeight: 32, // 标准控件高度
    },
    Table: {
      headerBg: '#f8fafc', // 表头灰底（规范 2.2 bg-base）
      headerColor: '#64748b', // 表头文字次要灰（规范 2.2 text-secondary）
      headerSplitColor: '#e2e8f0', // 表头分割线
      borderColor: '#e2e8f0', // 表格边框
      rowHoverBg: '#f8fafc', // 行 hover 中性底
      cellPaddingBlockSM: 7, // 紧凑表格纵向内边距
      cellPaddingInlineSM: 12, // 紧凑表格横向内边距
    },
    Tag: {
      // 去除全局 999px 胶囊 + display 字体，走默认；语义色仍由业务传入
      borderRadiusSM: 4, // 标签圆角（规范 圆角表）
    },
    Modal: {
      borderRadiusLG: 12, // 模态框圆角（规范 圆角表）
    },
    Card: {
      // 去除全局 hover 加大阴影，卡片默认无阴影用 border 分层
      boxShadowTertiary: 'none',
    },
  } as ThemeConfig['components'],
};
